import React from 'react'

import Link from '../../helper/link'
import Memory from '../../helper/memory'
import Milliseconds from '../../helper/milliseconds'
import Percent from '../../helper/percent'
import Shorten from '../../helper/shorten'
import { Box, BoxBody, BoxHeader, BoxTitle } from '../../ui/box'
import { XhprofStatsData } from '../../xhprof/XhprofProfiler'
import { XhprofCallInfo } from '../../xhprof/XhprofReportGenerator'
import { BottleneckIdentifications } from '../../domain/Trace'
import { IdeIntegrationButton } from '../../errors/IdeIntegrationButton'
import { DynamicallyInstrumentedFunction } from '../DynamicallyInstrumentedFunction'
import { BottleneckMessage, bottleneckMessageHasRelatedCalls } from '../../domain/BottleneckMessage'

interface OwnProps {
    call: string
    callInfo?: XhprofCallInfo
    statsData: XhprofStatsData
    onSelectNode: (call: string) => void
    children?: React.ReactNode
    stackTrace?: any
    params?: { [key: string]: string }
    bottleneckIdentifications?: BottleneckIdentifications
    bottlenecks: BottleneckMessage[]
    bottleneckRoute: string
    includesDynamicallyInstrumentedFunctions: boolean
}

interface DefaultProps {
    selectMode: string
    compareMode: boolean
    isRootCall: boolean
}

interface State {
    displayFoldedNodes: boolean
}

type Props = OwnProps & DefaultProps

export class SelectedCall extends React.Component<Props, State> {
    public static defaultProps: DefaultProps = {
        selectMode: 'full',
        compareMode: false,
        isRootCall: false,
    }

    public state: State = {
        displayFoldedNodes: false,
    }

    public render() {
        const { call, selectMode, callInfo, statsData, bottlenecks } = this.props

        const matchingBottlenecks = bottlenecks.filter(
            bottleneck => bottleneck.args && bottleneckMessageHasRelatedCalls(bottleneck.args) && call in bottleneck.args.relatedCalls,
        )

        // https://github.com/php/php-src/blob/944b6b6bbd6f05ad905f5f4ad07445792bee4027/Zend/zend_compile.c#L4291-L4353
        const compilerOptimizableFunctions = [
            'strlen',
            'is_null',
            'is_bool',
            'is_long',
            'is_int',
            'is_integer',
            'is_float',
            'is_double',
            'is_string',
            'is_array',
            'is_object',
            'is_resource',
            'is_scalar',
            'boolval',
            'intval',
            'doubleval',
            'floatval',
            'strval',
            'defined',
            'chr',
            'ord',
            'call_user_func_array',
            'call_user_func',
            'in_array',
            'sizeof',
            'count',
            'get_class',
            'get_called_class',
            'gettype',
            'func_num_args',
            'func_get_args',
            'array_slice',
            'array_key_exists',
            'sprintf',
        ]

        const details = {
            excl_pmu: statsData.excl_pmu ? statsData.excl_pmu : 0,
            name: statsData.name,
            namespace: statsData.namespace ? statsData.namespace : '',
            parents: statsData.parents,
            children: statsData.children,
            hasMemory: statsData.hasMemory ? statsData.hasMemory : false,
            wt: statsData.wt,
            perc_wt: statsData.perc_wt ? statsData.perc_wt : 0,
            excl_wt: statsData.excl_wt,
            perc_excl_wt: statsData.perc_excl_wt ? statsData.perc_excl_wt : 0,
            pmu: statsData.pmu ? statsData.pmu : 0,
            perc_pmu: statsData.perc_pmu ? statsData.perc_pmu : 0,
            perc_excl_pmu: statsData.perc_excl_pmu ? statsData.perc_excl_pmu : 0,
            ct: statsData.ct,
            perc_ct: statsData.perc_ct ? statsData.perc_ct : 0,
        }

        const foldedNodes =
            (selectMode === 'callgraph' || selectMode === 'callgraph-new') &&
            callInfo &&
            callInfo.foldedNodes &&
            callInfo.foldedNodes.length > 0
                ? callInfo.foldedNodes
                : []

        const bottleneckIdentification =
            this.props.bottleneckIdentifications != undefined ? this.props.bottleneckIdentifications[details.name || 'unknown'] : null

        return (
            <Box>
                <BoxHeader>
                    <div className="btn-group pull-right">
                        {selectMode === 'callgraph' ? (
                            <Link
                                className="btn btn-default"
                                route="XhprofProfilerBundle.Profile.full"
                                query={{ fn: call }}
                                title="Show call details"
                            >
                                <i className="fa fa-list" />
                            </Link>
                        ) : null}
                        {selectMode === 'full' ? (
                            <Link
                                className="btn btn-default"
                                route="XhprofProfilerBundle.Profile.callgraph"
                                query={{ fn: call }}
                                title="Show callgraph"
                            >
                                <i className="fa fa-sitemap" />
                            </Link>
                        ) : null}
                    </div>

                    <BoxTitle>
                        <div className="pull-right" style={{ marginTop: '-5px' }}>
                            <IdeIntegrationButton
                                traceLine={{ function: (details.namespace ? details.namespace + '\\' : '') + (details.name ?? '') }}
                                renderWhenJustFqcn={true}
                            />
                            {this.props.includesDynamicallyInstrumentedFunctions ? (
                                <DynamicallyInstrumentedFunction details={details} />
                            ) : null}
                        </div>
                        <Shorten string={details.name} />
                        {details.name?.includes('@') ? (
                            <span
                                className="tw-tooltip tw-tooltip-dataText tw-tooltip--top tw-color-grey-400 tw-ml-4"
                                data-tooltip-text="@ in function calls indicates the recursion level."
                            >
                                <i className="fa fa-question-circle tw-text-gray-400" />
                            </span>
                        ) : null}
                    </BoxTitle>

                    <BoxTitle className="namespace">
                        {selectMode === 'callgraph-new' ? (
                            <div className="number text-right pull-right">
                                <span className="text-mute">
                                    {details.ct === 0 && this.props.compareMode ? (
                                        <span>&plusmn; 0 call</span>
                                    ) : (
                                        this.showSign(details.ct) + details.ct + ' calls'
                                    )}
                                </span>
                            </div>
                        ) : null}
                        {details.name && compilerOptimizableFunctions.indexOf(details.name) !== -1 && details.perc_wt >= 0.05 ? (
                            <>
                                This function could be faster by relying on a compiler optimization{' '}
                                <a href="https://support.tideways.com/documentation/reference/observations/compiler-optimized-functions.html?utm_medium=app&utm_source=callgraph&utm_campaign=compiler-optimized-hint">
                                    [Link]
                                </a>
                            </>
                        ) : null}
                        {details.namespace || ''}&nbsp;
                        {foldedNodes.length > 0 ? (
                            <button
                                className="label label-outline-default"
                                onClick={() => this.setState({ displayFoldedNodes: !this.state.displayFoldedNodes })}
                            >
                                +{foldedNodes.length} folded calls
                            </button>
                        ) : null}
                    </BoxTitle>
                </BoxHeader>
                <BoxBody noPadding>
                    {matchingBottlenecks.length > 0 ? (
                        <div className="tw-m-8 compact-alert compact-alert-warning text-ellipsis">
                            <i className="fa fa-warning" /> We consider this call a{' '}
                            <Link route={this.props.bottleneckRoute} params={{ bottleneck: matchingBottlenecks[0].type }}>
                                bottleneck (see description for details)
                            </Link>
                            .
                        </div>
                    ) : null}
                    {this.state.displayFoldedNodes && foldedNodes.length > 0 ? (
                        <div className="tw-ml-8">
                            <p className="tw-font-weight-strong">
                                Folded Function-Calls{' '}
                                <i
                                    className="text-mute fa fa-question-circle"
                                    title="Callgraph Nodes are aggregated when their total time is almost identical"
                                />
                            </p>
                            <ul className="list-unstyled tw-mb-16 tw-ml-8" style={{ overflow: 'hidden' }}>
                                {foldedNodes.map(foldedCall => (
                                    <li key={foldedCall}>
                                        <a onClick={() => this.props.onSelectNode(foldedCall)}>
                                            <span className="tw-font-weight-normal">{foldedCall}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    <table className="table table-condensed table-hover">
                        <thead>
                            <tr>
                                <th>&nbsp;</th>
                                <th>&nbsp;</th>
                                <th className="text-right">total</th>
                                <th className="text-right">self</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th className="bg-blue text-center tw-valign-middle">
                                    <i className="fa fa-clock-o tw-font-size-16" />
                                </th>
                                <th className="tw-valign-middle">Time</th>
                                <td className="number">
                                    <Milliseconds
                                        value={details.wt}
                                        class="unit tw-font-size-16 tw-font-weight-normal"
                                        showPlusSign={this.props.compareMode}
                                        showNoValueAsHyphen={false}
                                    />
                                    <br />
                                    <Percent
                                        value={details.perc_wt / 100}
                                        class="tw-font-weight-normal text-mute"
                                        showPlusSign={this.props.compareMode}
                                    />
                                </td>
                                <td className="number">
                                    <Milliseconds
                                        value={details.excl_wt}
                                        class="unit tw-font-size-16 tw-font-weight-strong"
                                        showPlusSign={this.props.compareMode}
                                        showNoValueAsHyphen={false}
                                    />
                                    <br />
                                    <Percent
                                        value={details.perc_excl_wt / 100}
                                        class="tw-font-weight-strong text-mute"
                                        showPlusSign={this.props.compareMode}
                                    />
                                </td>
                            </tr>
                            {details.hasMemory ? (
                                <tr>
                                    <th className="bg-green text-center tw-valign-middle">
                                        <i className="fa fa-database tw-font-size-16" />
                                    </th>
                                    <th className="tw-valign-middle">Memory</th>
                                    <td className="number tw-valign-bottom">
                                        <Memory
                                            value={details.pmu}
                                            class="unit memory tw-font-size-16 tw-font-weight-normal"
                                            showPlusSign={this.props.compareMode}
                                        />
                                        <br />
                                        <Percent
                                            class="tw-font-weight-normal text-mute"
                                            value={details.perc_pmu ? details.perc_pmu / 100 : 0}
                                            showPlusSign={this.props.compareMode}
                                        />
                                    </td>
                                    <td className="number tw-valign-bottom">
                                        <Memory
                                            value={details.excl_pmu}
                                            class="unit memory tw-font-size-16 tw-font-weight-strong"
                                            showPlusSign={this.props.compareMode}
                                        />
                                        <br />
                                        <Percent
                                            class="text-mute tw-font-weight-strong"
                                            value={details.perc_excl_pmu ? details.perc_excl_pmu / 100 : 0}
                                            showPlusSign={this.props.compareMode}
                                        />
                                    </td>
                                </tr>
                            ) : null}
                            {selectMode !== 'callgraph-new' ? (
                                <tr>
                                    <th className="bg-purple text-center tw-valign-middle">
                                        <big>
                                            <i className="fa fa-code-fork" />
                                        </big>
                                    </th>
                                    <th className="tw-valign-middle">Calls</th>
                                    <td className="number" colSpan={2}>
                                        <span className="tw-font-size-16 tw-font-weight-strong">{details.ct}</span>
                                        <br />
                                        <Percent class="tw-font-weight-bold text-mute" value={details.perc_ct / 100} />
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                    {this.props.children}
                </BoxBody>
            </Box>
        )
    }

    private showSign = value => {
        return value >= 0 && this.props.compareMode ? '+' : ``
    }
}
